'use client'

import { useState } from 'react'

import { reloadPage } from '@marketplace-web/shared/browser'
import { reuploadItem as reuploadItemRequest } from '@marketplace-web/domain/order-details-data'
import { ResponseCode } from 'data/api/response-codes'

import { useTracking } from '@marketplace-web/shared/event-tracker'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import {
  ConversationActionButton,
  ConversationActionProps,
} from '@marketplace-web/domain/conversation-action'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { clickEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'

type Props = ConversationActionProps

const ReuploadItem = ({ primary, title, inverse, theme, size }: Props) => {
  const { track } = useTracking()

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id

  const [buttonTitle, setButtonTitle] = useState<string | null>(title)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isItemUploading, setIsItemUploading] = useState(false)

  const trackButtonClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.ReuploadItem,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const reuploadItem = async () => {
    trackButtonClick()

    if (!transactionId) return

    setIsDisabled(false)
    setIsItemUploading(true)

    const response = await reuploadItemRequest(transactionId)
    if (response.code === ResponseCode.Ok) {
      setIsItemUploading(false)
      setButtonTitle(title)
    }

    if ('errors' in response && response.code === ResponseCode.ValidationError) {
      // TODO: investigate where errors could be placed (instead of displaying them on button)
      setButtonTitle(response.errors[0]!.value)
      setIsDisabled(true)
      setIsItemUploading(false)
    }

    reloadPage()
  }

  if (!transactionId) return null

  return (
    <ConversationActionButton
      text={buttonTitle}
      primary={primary}
      disabled={isDisabled}
      size={size}
      theme={theme}
      inverse={inverse}
      isLoading={isItemUploading}
      onClick={reuploadItem}
      testId="conversation-actions-reupload-item"
    />
  )
}

export default ReuploadItem
